
export type SoundType = 'timer-start' | 'timer-pause' | 'timer-complete' | 'timer-reset' | 'button-click';

export interface AudioSettings {
  enabled: boolean;
  volume: number;
  soundPack: 'default' | 'minimal' | 'nature';
}

class AudioManager {
  private audioContext: AudioContext | null = null;
  private settings: AudioSettings;
  private soundCache: Map<string, AudioBuffer> = new Map();
  
  constructor() {
    this.settings = this.loadSettings();
  }

  private loadSettings(): AudioSettings {
    const saved = localStorage.getItem('audio-settings');
    return saved ? JSON.parse(saved) : {
      enabled: true,
      volume: 0.7,
      soundPack: 'default'
    };
  }

  private saveSettings(): void {
    localStorage.setItem('audio-settings', JSON.stringify(this.settings));
  }

  private async initAudioContext(): Promise<void> {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  // Create a simple beep sound programmatically
  private async createBeepSound(frequency: number = 800, duration: number = 0.2): Promise<AudioBuffer | null> {
    try {
      await this.initAudioContext();
      if (!this.audioContext) return null;

      const sampleRate = this.audioContext.sampleRate;
      const numSamples = sampleRate * duration;
      const buffer = this.audioContext.createBuffer(1, numSamples, sampleRate);
      const data = buffer.getChannelData(0);

      for (let i = 0; i < numSamples; i++) {
        const t = i / sampleRate;
        const envelope = Math.exp(-t * 3); // Exponential decay
        data[i] = Math.sin(2 * Math.PI * frequency * t) * envelope * 0.3;
      }

      return buffer;
    } catch (error) {
      console.warn('Error creating beep sound:', error);
      return null;
    }
  }

  private getSoundPath(type: SoundType): string {
    const soundMap = {
      'timer-start': `sounds/${this.settings.soundPack}/start.mp3`,
      'timer-pause': `sounds/${this.settings.soundPack}/pause.mp3`,
      'timer-complete': `sounds/${this.settings.soundPack}/complete.mp3`,
      'timer-reset': `sounds/${this.settings.soundPack}/reset.mp3`,
      'button-click': `sounds/${this.settings.soundPack}/click.mp3`
    };
    
    return soundMap[type];
  }

  private async loadSound(path: string): Promise<AudioBuffer | null> {
    try {
      if (this.soundCache.has(path)) {
        return this.soundCache.get(path)!;
      }

      const response = await fetch(path);
      if (!response.ok) {
        console.warn(`Failed to load sound: ${path}, using fallback`);
        return null;
      }
      
      const arrayBuffer = await response.arrayBuffer();
      await this.initAudioContext();
      
      if (!this.audioContext) return null;
      
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      this.soundCache.set(path, audioBuffer);
      return audioBuffer;
    } catch (error) {
      console.warn(`Error loading sound ${path}:`, error);
      return null;
    }
  }

  private getFallbackSound(type: SoundType): Promise<AudioBuffer | null> {
    // Create different beep sounds for different actions
    switch (type) {
      case 'timer-start':
        return this.createBeepSound(1000, 0.1); // High short beep
      case 'timer-pause':
        return this.createBeepSound(600, 0.1); // Lower short beep
      case 'timer-complete':
        return this.createBeepSound(800, 0.3); // Medium longer beep
      case 'timer-reset':
        return this.createBeepSound(400, 0.15); // Low medium beep
      case 'button-click':
        return this.createBeepSound(1200, 0.05); // Very high very short beep
      default:
        return this.createBeepSound(800, 0.2);
    }
  }

  async playSound(type: SoundType): Promise<void> {
    if (!this.settings.enabled) return;

    try {
      const soundPath = this.getSoundPath(type);
      let audioBuffer = await this.loadSound(soundPath);
      
      // If loading from file failed, use fallback sound
      if (!audioBuffer) {
        audioBuffer = await this.getFallbackSound(type);
      }
      
      if (!audioBuffer || !this.audioContext) return;

      const source = this.audioContext.createBufferSource();
      const gainNode = this.audioContext.createGain();
      
      source.buffer = audioBuffer;
      gainNode.gain.value = this.settings.volume;
      
      source.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      source.start();
    } catch (error) {
      console.warn(`Error playing sound ${type}:`, error);
    }
  }

  getSettings(): AudioSettings {
    return { ...this.settings };
  }

  updateSettings(newSettings: Partial<AudioSettings>): void {
    this.settings = { ...this.settings, ...newSettings };
    this.saveSettings();
  }

  setEnabled(enabled: boolean): void {
    this.settings.enabled = enabled;
    this.saveSettings();
  }

  setVolume(volume: number): void {
    this.settings.volume = Math.max(0, Math.min(1, volume));
    this.saveSettings();
  }

  setSoundPack(soundPack: AudioSettings['soundPack']): void {
    this.settings.soundPack = soundPack;
    this.soundCache.clear(); // Clear cache when switching sound packs
    this.saveSettings();
  }

  // Test method to play a sound for settings preview
  async testSound(): Promise<void> {
    await this.playSound('button-click');
  }
}

export const audioManager = new AudioManager();
